import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Calendar as AntdCalendar } from 'antd';
import styles from './UserAttendance.module.scss';
import { Dropdown } from '@/components/Dropdown';
import BlockUi from 'react-block-ui';
import { useDispatch, useSelector } from 'react-redux';
import { memberActionTypes } from '@store/Member/Member.actionTypes';
import firebase from '@config/firebase.config';
import { leaveBalanceService } from '@/services/leaveBalance';
import moment from 'moment-timezone';
import {
  userLeaveBalanceConfig,
  userLeaveColorConfig,
  userLeaveEnum,
  userLeaveLabelEnum,
} from './UserAttendance.constants';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';

import { useTeamMemberDetailsForSelect } from '@/hooks/useTeamMemberDetailsForSelect';
import { Button, Spinner } from 'reactstrap';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';

import classNames from 'classnames';
import {
  useDashboardOverviewPopSync,
  useDashboardOverviewUrlWrite,
} from '@/hooks/useDashboardOverviewUrlSync';
import { getDashboardOverviewFiltersFromSearch } from '@/utils/dashboardOverviewUrlFilters';
import { CiExport } from 'react-icons/ci';
import httpClient from '@/config/httpClient';
import { addNotification, notificationStates } from '@/components/Notification';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
});

export const UserAttendanceComponent = () => {
  const database = firebase.firestore();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => ({
    userProfile: state.auth?.profile,
    activeCompany: state.auth?.profile?.activeCompany,
    companyList: state.auth?.profile?.companyList,
    activeTeamId: state.team.selectedTeamID,
    activeUserId: state.team.activeUserID,
    all: state.team.all,
  }));
  const activeTeamId = user.activeTeamId ?? null;
  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(activeTeamId);

  const options = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .map(([uid, m]) => ({
        value: uid,
        label: m?.userName || m?.userEmailid || uid,
      }))
      .filter((o) => o.value)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const filtersFromUrl = useMemo(
    () => getDashboardOverviewFiltersFromSearch(location.search),
    [location.search]
  );

  const membersOptionsRef = useRef(options);
  const exportTypeRef = useRef(null);
  membersOptionsRef.current = options;
  const [exportLoading, setExportLoading] = useState(false);
  const [selectOption, setSelectOptions] = useState({});
  const [statess, setStatess] = useState({
    open: '',
    selectedTitle: '',
  });
  const [timeLogInfo, setTimeLogInfo] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCount, setShowCount] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const { start } = getDashboardOverviewFiltersFromSearch(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return start ? moment(start, 'YYYY-MM-DD').toDate() : new Date();
  });
  const isCalenderMonthCalledRef = useRef(false);

  const dateRange = useMemo(() => {
    const m = moment(currentMonth);
    return {
      start: m.clone().startOf('month').format('YYYY-MM-DD'),
      end: m.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }, [currentMonth]);
  const [, setIgnoreUrlTimezone] = useState(false);
  const [, setUrlMemberPlaceholder] = useState(null);

  const setDateRangeForPop = useCallback((updater) => {
    setCurrentMonth((prev) => {
      const curStart = moment(prev).startOf('month').format('YYYY-MM-DD');
      const curEnd = moment(prev).endOf('month').format('YYYY-MM-DD');
      const cur = { start: curStart, end: curEnd };
      const next = typeof updater === 'function' ? updater(cur) : updater;
      if (next?.start) return moment(next.start).toDate();
      return prev;
    });
  }, []);

  useDashboardOverviewPopSync({
    history,
    setDateRange: setDateRangeForPop,
    setIgnoreUrlTimezone,
    setSelectedMember: setUrlMemberPlaceholder,
    membersOptionsRef,
  });

  useDashboardOverviewUrlWrite({
    teamId: activeTeamId,
    memberId: selectOption?.value,
    dateRange,
    selectedTimezone: moment.tz.guess(),
    history,
    location,
  });
  const timeLogByDate = useMemo(() => {
    const map = new Map();
    timeLogInfo.forEach((item) => {
      map.set(item.date, item);
    });
    return map;
  }, [timeLogInfo]);
  const attendanceDateCellRender = useCallback(
    (current, info) => {
      if (info.type !== 'date') return null;
      const log = timeLogByDate.get(current.format('YYYY-MM-DD'));
      if (!log) return null;
      return (
        <div
          className={classNames(styles.dateCellWrapper, styles.event)}
          style={{ background: log.color }}
        >
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
          >
            {log.Status}
          </Text>
        </div>
      );
    },
    [timeLogByDate]
  );
  const isWeekend = (date) => {
    const dayOfWeek = date.day();
    return dayOfWeek === 6 || dayOfWeek === 0;
  };

  const isFutureDate = (date) => date.get() > new Date().getTime();

  const getMembersTimeInfo = async (month, year, userId) => {
    try {
      setIsLoading(true);
      const newDate = new Date(year, month, 0);
      const monthString = month.toString().padStart(2, '0');
      const daysInMonth = newDate.getDate();
      const formattedDays = [];
      for (let day = 1; day <= daysInMonth; day++) {
        formattedDays.push(
          `${day.toString().padStart(2, '0')}:${monthString}:${year}`
        );
      }
      const formattedMonth = `${newDate
        .toLocaleString('default', { month: 'long' })
        .toLowerCase()}-${year}`;

      const entireMonthDocData = await database
        .collection('membersDatabase')
        .doc(userId)
        .collection('attendanceByMonth')
        .doc(formattedMonth)
        .get();

      const entireMonthData = entireMonthDocData.data() || {};

      // Approved leave from company (single source)
      let approvedLeaveByDate = {};
      if (user?.activeCompany?.companyId) {
        try {
          const startDate = `01-${monthString}-${year}`;
          const endDate = `${String(daysInMonth).padStart(
            2,
            '0'
          )}-${monthString}-${year}`;
          const approvedLeave =
            await leaveBalanceService.getApprovedLeaveForUserInDateRange(
              user.activeCompany.companyId,
              userId,
              startDate,
              endDate
            );
          approvedLeave.forEach(({ leaveDate, type }) => {
            approvedLeaveByDate[leaveDate] = type;
          });
        } catch (e) {
          // ignore
        }
      }
      let newTimeLogInfo = [];
      for (let i = 0; i < formattedDays.length; i++) {
        const day = formattedDays[i];
        const data = entireMonthData[day];
        const momentDate = moment(day, 'DD:MM:YYYY');
        const date = momentDate.format('YYYY-MM-DD');
        let Status, color, key;
        const leaveType = approvedLeaveByDate[day];
        if (leaveType) {
          Status = leaveType; // show leave type e.g. "Paid Leave", "Sick Leave", "Casual Leave"
          if (leaveType === userLeaveLabelEnum.sickLeave) {
            color = userLeaveColorConfig[userLeaveEnum.sickLeave];
            key = userLeaveEnum.sickLeave;
          } else if (leaveType === userLeaveLabelEnum.casualLeave) {
            color = userLeaveColorConfig[userLeaveEnum.casualLeave];
            key = userLeaveEnum.casualLeave;
          } else if (leaveType === userLeaveLabelEnum.plannedLeave) {
            color = userLeaveColorConfig[userLeaveEnum.plannedLeave];
            key = userLeaveEnum.plannedLeave;
          } else if (leaveType === userLeaveLabelEnum.unpaidLeave) {
            color = userLeaveColorConfig[userLeaveEnum.unpaidLeave];
            key = userLeaveEnum.unpaidLeave;
          } else {
            color = userLeaveColorConfig[userLeaveEnum.absent];
            key = userLeaveEnum.absent;
          }
        } else if (data?.present === true) {
          Status = userLeaveLabelEnum.present;
          color = userLeaveColorConfig[userLeaveEnum.present];
          key = userLeaveEnum.present;
        } else if (isWeekend(momentDate)) {
          Status = userLeaveLabelEnum.weekOff;
          color = userLeaveColorConfig[userLeaveEnum.weekOff];
          key = userLeaveEnum.weekOff;
        } else if (isFutureDate(momentDate)) {
          Status = ' ';
          color = userLeaveColorConfig[userLeaveEnum.absent];
          key = userLeaveEnum.absent;
        } else {
          Status = userLeaveLabelEnum.absent;
          color = userLeaveColorConfig[userLeaveEnum.absent];
          key = userLeaveEnum.absent;
        }
        newTimeLogInfo.push({
          Status,
          date,
          color,
          key,
        });
      }
      newTimeLogInfo = newTimeLogInfo.filter((item) => item.Status !== ' ');
      setTimeLogInfo((prevData) => [...prevData, ...newTimeLogInfo]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   const date = new Date();
  //   if (selectOption && selectOption?.value) {
  //     getCalenderMonth(date, user?.userProfile?.id);
  //   }
  //   // return () => {};
  // }, [selectOption]);

  function getCalenderMonth(date, userId) {
    if (user?.activeTeamId && !isCalenderMonthCalledRef.current) {
      setTimeLogInfo([]);
      // const currentDate = new Date();
      // if (date.getTime() <= currentDate.getTime()
      // date.getFullYear() === currentDate.getFullYear() &&
      // date.getMonth() <= currentDate.getMonth()
      // ) {
      const month = date?.getMonth() + 1;
      const year = date?.getFullYear();
      getMembersTimeInfo(month, year, userId);
      isCalenderMonthCalledRef.current = true;
      // }
    }
  }

  const [dataFetched, setDataFetched] = useState(false);
  const [selectedUser, setSelectedUser] = useState(user);
  const handleSelectChange = (option) => {
    setSelectedUser(option);
    dispatch({
      type: memberActionTypes.GET_USER_TIMELOG_DATA,
      payload: {
        activeUserName: option?.label,
      },
    });
    setShowCount(true);
    setSelectOptions(option || {});
    setTimeLogInfo([]); // Reset the timeLogInfo state
    if (option && option.value) {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      if (!dataFetched) {
        getMembersTimeInfo(month, year, option.value);
        isCalenderMonthCalledRef.current = true;
      } else {
        setDataFetched(false); // Reset the dataFetched flag
        isCalenderMonthCalledRef.current = false;
      }
    }
  };

  // Set default member when options are ready; `member` query wins, then profile, then first.
  useEffect(() => {
    if (!activeTeamId) {
      setSelectOptions({});
      setSelectedUser({
        value: user.userProfile?.id,
        label: user.userProfile?.name,
      });
      return;
    }
    if (options.length === 0) return;
    if (
      selectOption?.value &&
      options.some((o) => o.value === selectOption.value)
    ) {
      return;
    }
    if (filtersFromUrl.member) {
      const fromUrl = options.find((o) => o.value === filtersFromUrl.member);
      if (fromUrl) {
        handleSelectChange(fromUrl);
        return;
      }
    }
    const defaultOption =
      options.find((o) => o.value === user.userProfile?.id) || options[0];
    if (defaultOption) {
      handleSelectChange(defaultOption);
    }
  }, [
    activeTeamId,
    options,
    user.userProfile?.id,
    user.userProfile?.name,
    selectOption?.value,
    filtersFromUrl.member,
  ]);

  const [leaveBalanceConfig, setLeaveBalanceConfig] = useState({
    present: 0,
    absent: 0,
    weekOff: 0,
    sickLeave: 0,
    paidLeave: 0,
    casualLeave: 0,
    plannedLeave: 0,
    unpaidLeave: 0,
  });

  const calculatePresentAndLeave = () => {
    const leaveBalanceConfig = {
      present: 0,
      absent: 0,
      weekOff: 0,
      sickLeave: 0,
      paidLeave: 0,
      casualLeave: 0,
      plannedLeave: 0,
      unpaidLeave: 0,
    };
    timeLogInfo.forEach((item) => {
      leaveBalanceConfig[item.key]++;
    });

    setLeaveBalanceConfig(leaveBalanceConfig);
  };

  useMemo(() => {
    calculatePresentAndLeave();
  }, [timeLogInfo]);

  let activeUser = {
    activeTeamId: user.activeTeamId,
    activeUserId: selectedUser.value ? selectedUser.value : user.userProfile.id,
  };
  const applySelectedMonth = (date) => {
    setIgnoreUrlTimezone(true);
    isCalenderMonthCalledRef.current = false;
    setCurrentMonth(date);
    getCalenderMonth(date, selectOption?.value);
  };
  if (activeTeamId && membersLoading && options.length === 0) {
    return (
      <div className="loader-container">
        <BlockUi tag="div" blocking message="Loading, please wait" />
      </div>
    );
  }
  const handleExport = async ({ exportType }) => {
    setExportLoading(true);
    try {
      const requestPayload = {
        exportType,
        loggedInUser: {
          id: user.userProfile?.id,
          email: user.userProfile?.email,
          name: user.userProfile?.name,
        },
        memberName: selectOption?.label,
        teamId: activeTeamId,
        memberId: selectOption?.value,
        companyId: user.activeCompany?.companyId,
        companyName: user.activeCompany?.companyName,
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear(),
      };

      await httpClient.post('/exportUserAttendanceCsv', {
        ...requestPayload,
      });
    } catch (error) {
      addNotification({
        message: 'Failed to export user attendance',
        level: notificationStates.error,
        autoDismiss: 4,
      });
    } finally {
      setExportLoading(false);
    }
  };

  const isViewingCurrentMonth = moment(currentMonth).isSame(moment(), 'month');

  return (
    <div className={styles.userAttendancePage}>
      {membersError && activeTeamId && (
        <div className="mb-1 text-danger small">
          Failed to load members. {membersErrorObj?.message || ''}{' '}
          <Button
            color="link"
            size="sm"
            className="p-0"
            onClick={() => refetchMembers()}
          >
            Retry
          </Button>
        </div>
      )}
      <div className={styles.filtersContainer}>
        <Dropdown
          label={'SELECT MEMBER'}
          value={selectOption}
          options={options}
          onChange={handleSelectChange}
          isLoading={membersLoading}
          isDisabled={!activeTeamId || membersError}
        />

        <div
          className={classNames(styles.todayButton, {
            [styles.todayButton_selected]: isViewingCurrentMonth,
          })}
          onClick={() => {
            applySelectedMonth(new Date());
          }}
        >
          Current Month
        </div>
        <div className={styles.monthSelector}>
          <div
            className={styles.monthSelectorIconContainer}
            onClick={() => {
              applySelectedMonth(
                moment(currentMonth).subtract(1, 'month').toDate()
              );
            }}
          >
            <MdArrowBack color="blue" />
          </div>
          <DatePicker
            className={styles.monthYearPicker}
            picker="month"
            allowClear={false}
            format="MMMM YYYY"
            value={dayjs(currentMonth)}
            onChange={(d) => {
              if (!d) return;
              applySelectedMonth(d.startOf('month').toDate());
            }}
          />
          <div
            className={styles.monthSelectorIconContainer}
            onClick={() => {
              applySelectedMonth(moment(currentMonth).add(1, 'month').toDate());
            }}
          >
            <MdArrowForward color="blue" />
          </div>
        </div>
      </div>
      {membersError && (
        <div className={styles.memberError}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            Failed to load members. {membersErrorObj?.message || ''}
          </Text>
          <Button color="link" size="sm" onClick={() => refetchMembers()}>
            Retry
          </Button>
        </div>
      )}
      <div className={styles.exportButtonContainer}>
        <div
          className={classNames(styles.exportButton, {
            [styles.exportButton_disabled]: exportTypeRef.current === 'team',
          })}
          style={{ minWidth: '100px' }}
          onClick={() => {
            setExportLoading(true);
            exportTypeRef.current = 'member';
            handleExport({
              exportType: 'member',
            });
          }}
        >
          {exportLoading && exportTypeRef.current === 'member' ? (
            <Spinner color="primary" size="sm" />
          ) : (
            <>
              <CiExport />
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Medium}
              >
                Export
              </Text>
            </>
          )}
        </div>
        <div
          className={classNames(styles.exportButton, {
            [styles.exportButton_disabled]: exportTypeRef.current === 'member',
          })}
          style={{ minWidth: '220px' }}
          onClick={() => {
            setExportLoading(true);
            exportTypeRef.current = 'team';
            handleExport({
              exportType: 'team',
            });
          }}
        >
          {exportLoading && exportTypeRef.current === 'team' ? (
            <Spinner color="primary" size="sm" />
          ) : (
            <>
              <CiExport />
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Medium}
              >
                Export Team Attendance
              </Text>
            </>
          )}
        </div>
      </div>
      <div className={styles.leaveBalanceContainer}>
        {userLeaveBalanceConfig.map((item) => {
          return (
            <div className={styles.leaveBalanceItem}>
              <div
                className={styles.colorDot}
                style={{ background: item.color }}
              />
              <Text
                variant={textVariantsEnum.Text_xs}
                fontWeight={fontWeightEnum.Normal}
              >
                {item.label.toUpperCase()}
              </Text>
              <Text
                variant={textVariantsEnum.Text_lg}
                fontWeight={fontWeightEnum.Bold}
              >
                {leaveBalanceConfig[item.key]}
              </Text>
            </div>
          );
        })}
      </div>
      <div className={styles.calendarContainer}>
        <div className={styles.calendarHeader}>
          <Text
            variant={textVariantsEnum.Display_sm}
            fontWeight={fontWeightEnum.Bold}
          >
            User Attendance
          </Text>
          <Text
            variant={textVariantsEnum.Display_sm}
            fontWeight={fontWeightEnum.Bold}
          >
            {moment(currentMonth).format('MMMM YYYY')}
          </Text>
        </div>
        {isLoading ? (
          <div className="loader-container">
            <BlockUi tag="div" blocking message="Loading, please wait" />
          </div>
        ) : (
          <div className={styles.antdCalendarRoot}>
            <AntdCalendar
              headerRender={() => null}
              value={dayjs(currentMonth)}
              mode="month"
              cellRender={attendanceDateCellRender}
              onPanelChange={(date) => {
                applySelectedMonth(date.startOf('month').toDate());
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
